import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Fab,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Fade,
  TablePagination
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Add as AddIcon,
  Factory as ManufacturingIcon,
  Build as BuildIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  LocalDrink as LocalDrinkIcon,
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  Factory as FactoryIcon,
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Download as DownloadIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { supabase } from '../../supabaseClient';

const statusColors = {
  pending: '#ff9800',
  approved: '#4caf50',
  rejected: '#f44336',
  in_progress: '#2196f3',
  completed: '#9c27b0'
};

const priorityColors = {
  low: '#9e9e9e',
  normal: '#2196f3',
  high: '#ff9800',
  urgent: '#f44336'
};

const RequestsContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  width: '100%',
  minHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
  }
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: 32,
  right: 32,
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: 'white',
  fontWeight: 500,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}`,
    transform: 'translateY(-2px)'
  },
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
}));

const BatchingRequests = () => {
  const { companyId } = useParams();
  const { canAccessPage, canEditPage, canDeletePage, hasPermission } = usePermissions();
  const theme = useTheme();

  // Check permissions for manufacturing orders
  const canViewManufacturingOrders = hasPermission('production.batch_requests.manufacturing_orders.view', 'read');
  const canEditManufacturingOrders = hasPermission('production.batch_requests.manufacturing_orders.edit', 'write');
  const canDeleteManufacturingOrders = hasPermission('production.batch_requests.manufacturing_orders.delete', 'delete');
  const canApproveManufacturingOrders = hasPermission('production.batch_requests.manufacturing_orders.approval', 'write');

  // State management  
  const [manufacturingRequests, setManufacturingRequests] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Data for dropdowns
  const [products, setProducts] = useState([]);
  const [formulations, setFormulations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [productVariants, setProductVariants] = useState([]);
  const [user, setUser] = useState(null);
  
  // Unit configuration states
  const [unitConfigurations, setUnitConfigurations] = useState([]);
  const [selectedUnitConfig, setSelectedUnitConfig] = useState(null);
  const [unitInputs, setUnitInputs] = useState({});
  
  // Dialog states for selection
  const [productSelectionDialogOpen, setProductSelectionDialogOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [formulationSelectionDialogOpen, setFormulationSelectionDialogOpen] = useState(false);
  const [formulationSearchTerm, setFormulationSearchTerm] = useState('');
  const [locationSelectionDialogOpen, setLocationSelectionDialogOpen] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  
  // Approval dialog states
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalRequest, setApprovalRequest] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  
  // Delete confirmation dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRequest, setDeleteRequest] = useState(null);
  
  // Pagination states
  const [manufacturingPage, setManufacturingPage] = useState(0);
  const [manufacturingRowsPerPage, setManufacturingRowsPerPage] = useState(10);

  // Form state for new request
  const [newRequest, setNewRequest] = useState({
    product_id: '',
    formulation_id: '',
    production_location: '',
    requested_quantity: 100,
    requested_unit: 'g',
    needed_by_date: null,
    reason: '',
    priority: 'normal',
    variant_distribution: [],
    status: 'pending',
    unit_configuration_id: null
  });


  useEffect(() => {
    fetchRequests();
    fetchProducts();
    fetchFormulations();
    fetchLocations();
    fetchUser();
    fetchUnitConfigurations();
  }, [companyId]);

  const fetchUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  const fetchRequests = async () => {
    try {
      if (canViewManufacturingOrders) {
        const { data: manufacturingData, error: manufacturingError } = await supabase
          .from('manufacturing_requests')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false });
        
        if (manufacturingError) throw manufacturingError;
        setManufacturingRequests(manufacturingData || []);

        // Fetch product variants for all manufacturing requests that have product_ids
        if (manufacturingData && manufacturingData.length > 0) {
          const uniqueProductIds = [...new Set(manufacturingData.map(req => req.product_id).filter(Boolean))];
          
          if (uniqueProductIds.length > 0) {
            const { data: variantsData, error: variantsError } = await supabase
              .from('product_variants')
              .select('*')
              .in('product_id', uniqueProductIds)
              .eq('is_active', true)
              .order('volume_weight', { ascending: false });
            
            if (variantsError) throw variantsError;
            setProductVariants(variantsData || []);
          }
        }
      } else {
        setManufacturingRequests([]);
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchFormulations = async () => {
    try {
      const { data, error } = await supabase
        .from('formulations')
        .select('*')
        .eq('company_id', companyId)
        .eq('status', 'approved')
        .order('name');
      
      if (error) throw error;
      setFormulations(data || []);
    } catch (err) {
      console.error('Error fetching formulations:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      
      // Sort so main locations appear first, then by name
      const sortedLocations = (data || []).sort((a, b) => {
        // Main locations first (is_main = true)
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        // Then sort by name
        return a.name.localeCompare(b.name);
      });
      
      setLocations(sortedLocations);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchUnitConfigurations = async () => {
    try {
      const { data, error } = await supabase
        .from('unit_configurations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      
      // Sort by total units per pallet (descending), then by name
      const sortedConfigs = (data || []).sort((a, b) => {
        const getTotalUnitsPerPallet = (config) => {
          if (config.enable_pallets && config.enable_cases) {
            return config.units_per_case * config.cases_per_pallet;
          } else if (config.enable_cases) {
            return config.units_per_case;
          } else {
            return 1;
          }
        };
        
        const aTotal = getTotalUnitsPerPallet(a);
        const bTotal = getTotalUnitsPerPallet(b);
        
        if (bTotal !== aTotal) {
          return bTotal - aTotal;
        }
        return a.name.localeCompare(b.name);
      });
      
      setUnitConfigurations(sortedConfigs);
    } catch (err) {
      console.error('Error fetching unit configurations:', err);
    }
  };

  const fetchProductVariants = async (productId) => {
    try {
      const { data, error } = await supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', productId)
        .eq('is_active', true)
        .order('volume_weight', { ascending: false });
      
      if (error) throw error;
      setProductVariants(data || []);
    } catch (err) {
      console.error('Error fetching product variants:', err);
      setProductVariants([]);
    }
  };

  const handleCreateRequest = () => {
    setNewRequest({
      product_id: '',
      formulation_id: '',
      production_location: '',
      requested_quantity: 100,
      requested_unit: 'g',
      needed_by_date: null,
      reason: '',
      priority: 'normal',
      variant_distribution: [],
      status: 'pending',
      unit_configuration_id: null
    });
    setIsEditing(false);
    setEditingRequest(null);
    setErrorMessage('');
    setProductVariants([]);
    setSelectedUnitConfig(null);
    setUnitInputs({});
    setCreateDialogOpen(true);
  };

  const handleSaveRequest = async () => {
    setSaving(true);
    setErrorMessage('');
    
    try {
      if (!newRequest.product_id || !newRequest.formulation_id || !newRequest.production_location || !newRequest.reason) {
        setErrorMessage('Please fill in all required fields: Product, Formulation, Location, and Reason.');
        setSaving(false);
        return;
      }
      
      if (productVariants.length > 0) {
        if (!newRequest.variant_distribution || newRequest.variant_distribution.length === 0) {
          setErrorMessage('Please specify quantities for at least one product variant.');
          setSaving(false);
          return;
        }
      }

      const requestData = {
        product_id: newRequest.product_id,
        formulation_id: newRequest.formulation_id,
        production_location: newRequest.production_location,
        requested_quantity: newRequest.requested_quantity,
        requested_unit: newRequest.requested_unit,
        needed_by_date: newRequest.needed_by_date ? newRequest.needed_by_date.format('YYYY-MM-DD') : null,
        reason: newRequest.reason,
        priority: newRequest.priority,
        status: newRequest.status,
        variant_distribution: newRequest.variant_distribution,
        unit_configuration_id: newRequest.unit_configuration_id || null,
        company_id: companyId,
        created_by: user?.id,
        updated_by: user?.id,
      };
      
      let data, error;

      if (isEditing && editingRequest) {
        const updateData = { ...requestData };
        delete updateData.created_by;
        
        const result = await supabase
          .from('manufacturing_requests')
          .update(updateData)
          .eq('id', editingRequest.id)
          .select();
        
        data = result.data;
        error = result.error;
      } else {
        const result = await supabase
          .from('manufacturing_requests')
          .insert([requestData])
          .select();
        
        data = result.data;
        error = result.error;
      }
      
      if (error) throw error;
      
      setCreateDialogOpen(false);
      setEditingRequest(null);
      setIsEditing(false);
      setProductVariants([]);
      await fetchRequests();
    } catch (error) {
      console.error('Error saving request:', error);
      setErrorMessage(error.message || 'An error occurred while saving the request. Please try again.');
    } finally {
      setSaving(false);
    }
  };


  const getStatusColor = (status) => {
    return statusColors[status] || statusColors.pending;
  };

  const getPriorityColor = (priority) => {
    return priorityColors[priority] || priorityColors.normal;
  };

  const handleViewRequest = (request) => {
    setViewingRequest(request);
    if (request.product_id) {
      fetchProductVariants(request.product_id);
    }
    setViewDialogOpen(true);
  };

  const handleEditRequest = (request) => {
    setEditingRequest(request);
    
    setNewRequest({
      ...request,
      needed_by_date: request.needed_by_date ? dayjs(request.needed_by_date) : null,
      status: (canApproveManufacturingOrders && request.status !== 'approved') ? request.status : 'pending'
    });
    
    if (request.product_id) {
      fetchProductVariants(request.product_id);
      
      // Load unit configuration if it exists
      if (request.unit_configuration_id) {
        const config = unitConfigurations.find(c => c.id === request.unit_configuration_id);
        if (config) {
          setSelectedUnitConfig(config);
          
          // Calculate unit inputs breakdown from variant_distribution
          if (request.variant_distribution && Array.isArray(request.variant_distribution)) {
            const calculatedInputs = {};
            
            request.variant_distribution.forEach(variantDist => {
              const totalUnits = variantDist.quantity || 0;
              
              // Calculate breakdown
              let remaining = totalUnits;
              let pallets = 0;
              let cases = 0;
              let units = 0;

              if (config.enable_pallets && config.enable_cases) {
                const unitsPerPallet = config.units_per_case * config.cases_per_pallet;
                pallets = Math.floor(remaining / unitsPerPallet);
                remaining = remaining % unitsPerPallet;
              }

              if (config.enable_cases) {
                cases = Math.floor(remaining / config.units_per_case);
                remaining = remaining % config.units_per_case;
              }

              units = remaining;

              calculatedInputs[variantDist.variant_id] = {
                pallets: pallets,
                cases: cases,
                units: units
              };
            });
            
            setUnitInputs(calculatedInputs);
          } else {
            setUnitInputs({});
          }
        }
      } else {
        setSelectedUnitConfig(null);
        setUnitInputs({});
      }
    }
    
    setIsEditing(true);
    setErrorMessage('');
    setViewDialogOpen(false);
    setCreateDialogOpen(true);
  };

  const handleDeleteRequest = (request) => {
    setDeleteRequest(request);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteRequest = async () => {
    try {
      const { error } = await supabase
        .from('manufacturing_requests')
        .delete()
        .eq('id', deleteRequest.id);

      if (error) throw error;

      fetchRequests();
      setDeleteDialogOpen(false);
      setDeleteRequest(null);
    } catch (error) {
      console.error('Error deleting request:', error);
      setErrorMessage(error.message || 'Failed to delete request');
    }
  };

  const handleReviewRequest = (request) => {
    setApprovalRequest(request);
    setSignatureName('');
    setApprovalNotes('');
    setApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    try {
      if (!signatureName.trim()) {
        setErrorMessage('Signature name is required for approval');
        return;
      }

      const approvalData = {
        approved_by_signature: signatureName,
        approved_at: new Date().toISOString(),
        approval_notes: approvalNotes || null,
        status: 'approved'
      };

      // Approve manufacturing request
      const { error: updateError } = await supabase
        .from('manufacturing_requests')
        .update(approvalData)
        .eq('id', approvalRequest.id);

      if (updateError) throw updateError;

      // Create batch calendar entry with unit configuration and variant distribution
      const batchData = {
        company_id: companyId,
        batch_number: `BR-${Date.now()}`,
        lot_number: `LOT-${Date.now()}`,
        product_id: approvalRequest.product_id,
        formulation_id: approvalRequest.formulation_id,
        production_location: approvalRequest.production_location,
        planned_start_date: approvalRequest.needed_by_date,
        planned_start_time: '09:00',
        planned_completion_date: approvalRequest.needed_by_date,
        planned_completion_time: '17:00',
        manufacture_date: approvalRequest.needed_by_date,
        status: 'planned',
        priority: approvalRequest.priority,
        planned_quantity: approvalRequest.requested_quantity || 100,
        planned_unit_of_measure: approvalRequest.requested_unit || 'g',
        variant_distribution: approvalRequest.variant_distribution || [],
        unit_configuration_id: approvalRequest.unit_configuration_id || null,
        product_variant_id: approvalRequest.product_variant_id || null,
        product_variant: approvalRequest.product_variant || null,
        created_by: user?.id,
        updated_by: user?.id
      };

      const { error: batchError } = await supabase
        .from('batch_calendar')
        .insert([batchData]);

      if (batchError) throw batchError;

      setApprovalDialogOpen(false);
      fetchRequests();
      setErrorMessage('');
    } catch (error) {
      console.error('Error approving request:', error);
      setErrorMessage(error.message || 'Failed to approve request');
    }
  };

  // Product selection handlers
  const handleOpenProductSelection = () => {
    setProductSearchTerm('');
    setProductSelectionDialogOpen(true);
  };

  const handleSelectProduct = async (product) => {
    setNewRequest(prev => ({ 
      ...prev, 
      product_id: product.id,
      variant_distribution: [] // Clear variant distribution when product changes
    }));
    await fetchProductVariants(product.id);
    setUnitInputs({}); // Reset unit inputs when product changes
    setProductSelectionDialogOpen(false);
  };

  const getFilteredProducts = () => {
    if (!productSearchTerm.trim()) return products;
    const searchTerm = productSearchTerm.toLowerCase();
    return products.filter(product => 
      product.name?.toLowerCase().includes(searchTerm) ||
      product.description?.toLowerCase().includes(searchTerm) ||
      product.sku?.toLowerCase().includes(searchTerm)
    );
  };

  // Formulation selection handlers
  const handleOpenFormulationSelection = () => {
    setFormulationSearchTerm('');
    setFormulationSelectionDialogOpen(true);
  };

  const handleSelectFormulation = (formulation) => {
    setNewRequest(prev => ({ 
      ...prev, 
      formulation_id: formulation.id,
      requested_unit: formulation.unit_of_measure || 'g'
    }));
    setFormulationSelectionDialogOpen(false);
  };

  const getFilteredFormulations = () => {
    if (!formulationSearchTerm.trim()) return formulations;
    const searchTerm = formulationSearchTerm.toLowerCase();
    return formulations.filter(formulation => 
      formulation.name?.toLowerCase().includes(searchTerm) ||
      formulation.version?.toLowerCase().includes(searchTerm) ||
      formulation.description?.toLowerCase().includes(searchTerm)
    );
  };

  // Location selection handlers
  const handleOpenLocationSelection = () => {
    setLocationSearchTerm('');
    setLocationSelectionDialogOpen(true);
  };

  const handleSelectLocation = (location) => {
    setNewRequest(prev => ({ ...prev, production_location: location.id }));
    setLocationSelectionDialogOpen(false);
  };

  const getFilteredLocations = () => {
    if (!locationSearchTerm.trim()) return locations;
    const searchTerm = locationSearchTerm.toLowerCase();
    return locations.filter(location => 
      location.name?.toLowerCase().includes(searchTerm) ||
      location.facility_type?.toLowerCase().includes(searchTerm) ||
      location.city?.toLowerCase().includes(searchTerm) ||
      location.state?.toLowerCase().includes(searchTerm)
    );
  };

  // Variant distribution handlers
  const getVariantQuantity = (variantId) => {
    const distribution = newRequest.variant_distribution || [];
    const variant = distribution.find(d => d.variant_id === variantId);
    return variant ? variant.quantity : 0;
  };

  const getVariantInputValue = (variantId) => {
    return getVariantQuantity(variantId) || '';
  };

  const handleVariantInputChange = (variantId, inputValue) => {
    const quantity = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
    
    const currentDistribution = newRequest.variant_distribution || [];
    let newDistribution;
    
    const existingIndex = currentDistribution.findIndex(d => d.variant_id === variantId);
    
    if (existingIndex >= 0) {
      if (quantity > 0) {
        newDistribution = [...currentDistribution];
        newDistribution[existingIndex] = { variant_id: variantId, quantity };
      } else {
        newDistribution = currentDistribution.filter(d => d.variant_id !== variantId);
      }
    } else if (quantity > 0) {
      newDistribution = [...currentDistribution, { variant_id: variantId, quantity }];
    } else {
      newDistribution = currentDistribution;
    }
    
    setNewRequest(prev => ({
      ...prev,
      variant_distribution: newDistribution,
    }));
  };

  const calculateTotalProduction = () => {
    const distribution = newRequest.variant_distribution || [];
    if (distribution.length === 0) {
      return { totalUnits: 0, totalVolume: 0, productionUnit: 'g' };
    }
    
    const totalUnits = distribution.reduce((sum, d) => sum + d.quantity, 0);
    const totalVolume = distribution.reduce((sum, d) => {
      const variant = productVariants.find(v => v.id === d.variant_id);
      return variant ? sum + (d.quantity * variant.volume_weight) : sum;
    }, 0);
    
    const firstVariant = productVariants[0];
    const productionUnit = firstVariant ? firstVariant.volume_unit : 'g';
    
    return { totalUnits, totalVolume, productionUnit };
  };

  const calculateTotalUnitsFromInputs = (variantId, inputsOverride = null) => {
    if (!selectedUnitConfig || !variantId) return 0;
    
    const variantInputs = inputsOverride || unitInputs[variantId] || { pallets: 0, cases: 0, units: 0 };
    let total = 0;
    
    // Add units from pallets
    if (selectedUnitConfig.enable_pallets && selectedUnitConfig.enable_cases) {
      total += (variantInputs.pallets || 0) * selectedUnitConfig.cases_per_pallet * selectedUnitConfig.units_per_case;
    }
    
    // Add units from cases
    if (selectedUnitConfig.enable_cases) {
      total += (variantInputs.cases || 0) * selectedUnitConfig.units_per_case;
    }
    
    // Add individual units
    total += (variantInputs.units || 0);
    
    return total;
  };

  const updateVariantFromUnitInputs = (variantId, newInputs) => {
    if (!selectedUnitConfig) return;
    
    const totalUnits = calculateTotalUnitsFromInputs(variantId, newInputs);
    
    // Update only this specific variant in the distribution
    const currentDistribution = newRequest.variant_distribution || [];
    let newDistribution = [...currentDistribution];
    
    // Find existing entry for this variant
    const existingIndex = newDistribution.findIndex(d => d.variant_id === variantId);
    
    if (totalUnits > 0) {
      if (existingIndex >= 0) {
        // Update existing entry
        newDistribution[existingIndex] = { variant_id: variantId, quantity: totalUnits };
      } else {
        // Add new entry
        newDistribution.push({ variant_id: variantId, quantity: totalUnits });
      }
    } else {
      // Remove entry if quantity is 0
      if (existingIndex >= 0) {
        newDistribution.splice(existingIndex, 1);
      }
    }
    
    const totalProduction = calculateTotalProductionFromDistribution(newDistribution);
    
    setNewRequest(prev => ({
      ...prev,
      variant_distribution: newDistribution,
      requested_quantity: totalProduction.totalVolume,
      requested_unit: totalProduction.productionUnit
    }));
  };

  const calculateTotalProductionFromDistribution = (distribution) => {
    let totalUnits = 0;
    let totalVolume = 0;
    
    distribution.forEach(entry => {
      const variant = productVariants.find(v => v.id === entry.variant_id);
      if (variant) {
        totalUnits += entry.quantity;
        totalVolume += entry.quantity * variant.volume_weight;
      }
    });
    
    // Get production unit from variants, or fallback to formulation unit or 'g'
    const selectedFormulation = formulations.find(f => f.id === newRequest.formulation_id);
    const productionUnit = productVariants[0]?.volume_unit || selectedFormulation?.unit_of_measure || newRequest.requested_unit || 'g';
    
    return { 
      totalUnits, 
      totalVolume,
      productionUnit
    };
  };

  const formatQuantityWithBreakdown = (request) => {
    // Helper function to get singular/plural form
    const getUnitName = (pluralName, quantity) => {
      if (quantity === 1) {
        // Convert plural to singular
        if (pluralName.endsWith('ies')) return pluralName.slice(0, -3) + 'y';
        if (pluralName.endsWith('es')) {
          // Handle cases like "boxes" -> "box", "cases" -> "case"
          if (pluralName.endsWith('ses') || pluralName.endsWith('xes') || pluralName.endsWith('zes')) {
            return pluralName.slice(0, -2);
          }
          return pluralName.slice(0, -2);
        }
        if (pluralName.endsWith('s')) return pluralName.slice(0, -1);
        return pluralName;
      }
      return pluralName;
    };

    // If no unit configuration, show simple quantity
    if (!request.unit_configuration_id || !request.variant_distribution || request.variant_distribution.length === 0) {
      const quantity = request.requested_quantity || 0;
      const unit = request.requested_unit || 'units';
      const unitName = getUnitName(unit, quantity);
      return `${quantity} ${unitName}`;
    }

    const config = unitConfigurations.find(c => c.id === request.unit_configuration_id);
    if (!config) {
      const quantity = request.requested_quantity || 0;
      const unit = request.requested_unit || 'units';
      const unitName = getUnitName(unit, quantity);
      return `${quantity} ${unitName}`;
    }

    // Calculate total units across all variants
    const totalUnits = request.variant_distribution.reduce((sum, d) => sum + (d.quantity || 0), 0);

    // Calculate breakdown with whole numbers for cases and units
    let remaining = totalUnits;

    if (config.enable_pallets && config.enable_cases) {
      const unitsPerPallet = config.units_per_case * config.cases_per_pallet;
      
      // Get the integer pallets and calculate remaining
      const wholePallets = Math.floor(totalUnits / unitsPerPallet);
      remaining = totalUnits - (wholePallets * unitsPerPallet);
      
      // Calculate whole cases and units from remaining
      const wholeCases = Math.floor(remaining / config.units_per_case);
      const wholeUnits = remaining % config.units_per_case;

      const palletPlural = config.pallet_unit_name || 'pallets';
      const casePlural = config.case_unit_name || 'cases';
      const unitPlural = config.base_unit_name || 'units';

      // Build the display string with whole numbers only and proper singular/plural
      let result = '';
      if (wholePallets > 0) {
        const palletName = getUnitName(palletPlural, wholePallets);
        result = `${wholePallets} ${palletName}`;
      }
      if (wholeCases > 0) {
        const caseName = getUnitName(casePlural, wholeCases);
        if (result) result += ` + ${wholeCases} ${caseName}`;
        else result = `${wholeCases} ${caseName}`;
      }
      if (wholeUnits > 0) {
        const unitName = getUnitName(unitPlural, wholeUnits);
        if (result) result += ` + ${wholeUnits} ${unitName}`;
        else result = `${wholeUnits} ${unitName}`;
      }
      if (!result) {
        const palletName = getUnitName(palletPlural, 0);
        result = `0 ${palletName}`;
      }
      return result;
    } else if (config.enable_cases) {
      // For cases only, show whole cases and whole units
      const wholeCases = Math.floor(totalUnits / config.units_per_case);
      const wholeUnits = totalUnits % config.units_per_case;
      
      const casePlural = config.case_unit_name || 'cases';
      const unitPlural = config.base_unit_name || 'units';

      const caseName = getUnitName(casePlural, wholeCases);
      let result = `${wholeCases} ${caseName}`;
      if (wholeUnits > 0) {
        const unitName = getUnitName(unitPlural, wholeUnits);
        result += ` + ${wholeUnits} ${unitName}`;
      }
      return result;
    }

    // Fallback: just show total units
    const unitPlural = config.base_unit_name || 'units';
    const unitName = getUnitName(unitPlural, totalUnits);
    return `${totalUnits} ${unitName}`;
  };


  // Pagination handlers for manufacturing requests
  const handleManufacturingPageChange = (event, newPage) => {
    setManufacturingPage(newPage);
  };

  const handleManufacturingRowsPerPageChange = (event) => {
    setManufacturingRowsPerPage(parseInt(event.target.value, 10));
    setManufacturingPage(0);
  };


  const renderManufacturingRequests = () => (
    <TableContainer sx={{ 
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '@keyframes slideInUp': {
        '0%': {
          transform: 'translateY(100%)',
          opacity: 0,
        },
        '100%': {
          transform: 'translateY(0)',
          opacity: 1,
        }
      }
    }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell 
              sx={{ 
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 1.5,
                px: 2.5
              }}
            >
              Product
            </TableCell>
            <TableCell 
              sx={{ 
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 1.5,
                px: 2.5
              }}
            >
              Quantity
            </TableCell>
            <TableCell 
              sx={{ 
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 1.5,
                px: 2.5
              }}
            >
              Needed By
            </TableCell>
            <TableCell 
              sx={{ 
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 1.5,
                px: 2.5
              }}
            >
              Priority
            </TableCell>
            <TableCell 
              sx={{ 
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 1.5,
                px: 2.5
              }}
            >
              Status
            </TableCell>
            <TableCell 
              align="center"
              sx={{ 
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 1.5,
                px: 2.5,
                width: 120
              }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {manufacturingRequests.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No manufacturing requests found.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            (manufacturingRowsPerPage > 0
              ? manufacturingRequests.slice(manufacturingPage * manufacturingRowsPerPage, manufacturingPage * manufacturingRowsPerPage + manufacturingRowsPerPage)
              : manufacturingRequests
            ).map((request) => (
              <TableRow 
                key={request.id} 
                hover
                onClick={() => handleViewRequest(request)}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    bgcolor: 'grey.50',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease',
                    boxShadow: 'inset 4px 0 0 black'
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    py: 2,
                    px: 2.5
                  }
                }}
              >
                <TableCell>
                  <Box>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 500,
                        color: 'text.primary',
                        fontSize: '0.95rem'
                      }}
                    >
                      {(() => {
                        const product = products.find(p => p.id === request.product_id);
                        if (!product) return 'Unknown Product';
                        
                        // Check if there are variants in the distribution
                        if (request.variant_distribution && Array.isArray(request.variant_distribution) && request.variant_distribution.length > 0) {
                          const variantNames = request.variant_distribution
                            .map(v => {
                              // Try to find matching variant to get volume_weight + volume_unit format
                              const matchingVariant = productVariants.find(pv => pv.id === v.variant_id);
                              if (matchingVariant) {
                                return `${matchingVariant.volume_weight}${matchingVariant.volume_unit}`;
                              }
                              // Fallback to stored data
                              return v.variant_name || v.name || v.size || `${v.volume_weight || ''}${v.volume_unit || ''}`;
                            })
                            .filter(name => name && name.trim())
                            .join(', ');
                          if (variantNames) {
                            return `${product.name} (${variantNames})`;
                          }
                        }
                        
                        return product.name;
                      })()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formulations.find(f => f.id === request.formulation_id)?.name || 'Unknown Formulation'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.9rem'
                    }}
                  >
                    {formatQuantityWithBreakdown(request)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.9rem'
                    }}
                  >
                    {request.needed_by_date ? dayjs(request.needed_by_date).format('MMM D, YYYY') : 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={request.priority?.toUpperCase()}
                    size="small"
                    sx={{
                      backgroundColor: getPriorityColor(request.priority),
                      color: 'white',
                      fontWeight: 500,
                      borderRadius: 2
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={request.status?.replace(/_/g, ' ').toUpperCase()}
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor(request.status),
                      color: 'white',
                      fontWeight: 500
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <IconButton
                      size="small"
                      disabled={!canEditManufacturingOrders}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (canEditManufacturingOrders) {
                          handleEditRequest(request);
                        }
                      }}
                      sx={{
                        color: canEditManufacturingOrders ? 'text.primary' : 'text.disabled',
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                        borderRadius: 2,
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        opacity: canEditManufacturingOrders ? 1 : 0.5,
                        cursor: canEditManufacturingOrders ? 'pointer' : 'not-allowed',
                        '&:hover': canEditManufacturingOrders ? {
                          bgcolor: 'primary.main',
                          color: 'white',
                          transform: 'scale(1.05)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                        } : {},
                        '&:disabled': {
                          color: 'text.disabled',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                          backgroundColor: alpha(theme.palette.grey[300], 0.3)
                        }
                      }}
                    >
                      <EditIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={!canDeleteManufacturingOrders}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (canDeleteManufacturingOrders) {
                          handleDeleteRequest(request);
                        }
                      }}
                      sx={{
                        color: canDeleteManufacturingOrders ? 'text.primary' : 'grey.400',
                        border: `1px solid ${alpha(canDeleteManufacturingOrders ? theme.palette.grey[400] : theme.palette.grey[300], 0.3)}`,
                        borderRadius: 2,
                        opacity: canDeleteManufacturingOrders ? 1 : 0.5,
                        cursor: canDeleteManufacturingOrders ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        ...(canDeleteManufacturingOrders && {
                          '&:hover': {
                            bgcolor: 'error.main',
                            color: 'white',
                            transform: 'scale(1.05)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.25)}`
                          }
                        })
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                    {request.status !== 'approved' && (
                      <IconButton
                        size="small"
                        disabled={!canApproveManufacturingOrders}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canApproveManufacturingOrders) {
                            handleReviewRequest(request);
                          }
                        }}
                        sx={{
                          color: canApproveManufacturingOrders ? 'text.primary' : 'grey.400',
                          border: `1px solid ${alpha(canApproveManufacturingOrders ? theme.palette.grey[400] : theme.palette.grey[300], 0.3)}`,
                          borderRadius: 2,
                          opacity: canApproveManufacturingOrders ? 1 : 0.5,
                          cursor: canApproveManufacturingOrders ? 'pointer' : 'not-allowed',
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          ...(canApproveManufacturingOrders && {
                            '&:hover': {
                              bgcolor: 'success.main',
                              color: 'white',
                              transform: 'scale(1.05)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.25)}`
                            }
                          })
                        }}
                      >
                        <CheckIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      
      <Box sx={{ 
        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
        borderRadius: '0 0 12px 12px'
      }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={manufacturingRequests.length}
          rowsPerPage={manufacturingRowsPerPage}
          page={manufacturingPage}
          onPageChange={handleManufacturingPageChange}
          onRowsPerPageChange={handleManufacturingRowsPerPageChange}
          sx={{ 
            px: 3,
            py: 2,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.9rem',
              fontWeight: 500,
              color: theme.palette.text.primary
            },
            '& .MuiTablePagination-select': {
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              borderRadius: 2,
              px: 2,
              py: 1,
              fontWeight: 500,
              fontSize: '0.9rem',
              background: 'white',
              '&:hover': {
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
              }
            },
            '& .MuiTablePagination-actions': {
              '& .MuiIconButton-root': {
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                borderRadius: 2,
                margin: '0 2px',
                background: 'white',
                color: theme.palette.primary.main,
                fontWeight: 500,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: theme.palette.primary.main,
                  color: 'white',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                },
                '&.Mui-disabled': {
                  background: alpha(theme.palette.grey[300], 0.5),
                  color: theme.palette.grey[500],
                  border: `1px solid ${theme.palette.grey[300]}`,
                  transform: 'none',
                  boxShadow: 'none'
                }
              }
            }
          }}
        />
      </Box>
    </TableContainer>
  );

  // Permission check
  if (!canViewManufacturingOrders) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Requests Access Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Request features. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
          </Card>
        </Box>
      </Container>
    );
  }


  return (
    <Fade in timeout={800}>
      <Box>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <RequestsContainer sx={{ width: '100%', minHeight: '80vh', mt: 4 }}>
          {/* Header */}
          <Box sx={{
            p: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.8)})`,
            borderRadius: '12px 12px 0 0',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
              zIndex: 0
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: 'info.main',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 }
                  }
                }} />
                <Typography variant="caption" sx={{ 
                  color: 'info.main', 
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontSize: '0.75rem'
                }}>
                  Request Management
                </Typography>
              </Box>
              
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                fontSize: '1.8rem',
                mb: 1,
                lineHeight: 1.2
              }}>
                Requests
              </Typography>
              
              <Typography variant="body2" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '0.9rem',
                mb: 2.5,
                maxWidth: '400px'
              }}>
                Manage manufacturing orders
              </Typography>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${manufacturingRequests.length} Manufacturing ${manufacturingRequests.length === 1 ? 'Order' : 'Orders'}`} 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: 'info.main',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                  }} 
                />
                <Chip 
                  label={`${manufacturingRequests.filter(r => r.status === 'pending').length} Pending`} 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha(theme.palette.grey[500], 0.1),
                    color: 'text.secondary',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`
                  }} 
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative', zIndex: 1 }}>
              {canEditManufacturingOrders && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateRequest}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    color: 'white',
                    fontWeight: 500,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Add Manufacturing Order
                </Button>
              )}
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 3 }}>
            {renderManufacturingRequests()}
          </Box>
        </RequestsContainer>


        {/* Create/Edit Dialog */}
        <Dialog 
          open={createDialogOpen} 
          onClose={() => {
            setCreateDialogOpen(false);
            setErrorMessage('');
          }}
          maxWidth="lg"
          fullWidth
          disableScrollLock={true}
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
              maxHeight: '90vh',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 500,
            fontSize: '1.2rem',
            color: 'primary.main',
            borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: alpha(theme.palette.primary.main, 0.05),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3
          }}>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              {isEditing ? 'Edit' : 'Create'} Manufacturing Order
            </Typography>
          </DialogTitle>
          
          <DialogContent sx={{ 
            backgroundColor: alpha(theme.palette.grey[50], 0.5), 
            p: 3,
            maxHeight: '80vh', 
            overflowY: 'auto',
            backdropFilter: 'blur(10px)',
            mt: 3
          }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 3, color: 'black' }} onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Basic Information */}
                  <Grid item xs={12}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[400]}`, 
                      borderRadius: 3, 
                      backgroundColor: 'white',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                      }
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Product & Formulation
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Product *"
                            value={newRequest.product_id ? products.find(p => p.id === newRequest.product_id)?.name || '' : ''}
                            placeholder="Click to select product..."
                            onClick={handleOpenProductSelection}
                            InputProps={{
                              readOnly: true,
                              sx: {
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                }
                              }
                            }}
                            sx={{
                              cursor: 'pointer',
                              '& .MuiOutlinedInput-root': {
                                cursor: 'pointer'
                              },
                              '& .MuiInputBase-input': {
                                cursor: 'pointer'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Formulation *"
                            value={newRequest.formulation_id ? 
                              (() => {
                                const formulation = formulations.find(f => f.id === newRequest.formulation_id);
                                return formulation ? `${formulation.name} v${formulation.version}` : '';
                              })() : ''
                            }
                            placeholder="Click to select formulation..."
                            onClick={handleOpenFormulationSelection}
                            InputProps={{
                              readOnly: true,
                              sx: {
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                }
                              }
                            }}
                            sx={{
                              cursor: 'pointer',
                              '& .MuiOutlinedInput-root': {
                                cursor: 'pointer'
                              },
                              '& .MuiInputBase-input': {
                                cursor: 'pointer'
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>

                  {/* UNIT CONFIGURATION SELECTION */}
                  {newRequest.product_id && productVariants.length > 0 && (
                    <Grid item xs={12}>
                      <Card sx={{ 
                        p: 3, 
                        border: `1px solid ${theme.palette.grey[400]}`, 
                        borderRadius: 3, 
                        backgroundColor: 'white',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                        }
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                          Production Configuration
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            {unitConfigurations.length === 0 ? (
                              <Box sx={{
                                textAlign: 'center',
                                py: 6,
                                px: 4,
                                border: '2px dashed',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.grey[50], 0.5)
                              }}>
                                <SettingsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                                  No Unit Configurations Found
                        </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 300, mx: 'auto' }}>
                                  Create unit configurations to define packaging structures.
                                </Typography>
                              </Box>
                            ) : (
                        <Grid container spacing={2}>
                                {unitConfigurations.map((config) => {
                                  const isSelected = selectedUnitConfig?.id === config.id;

                                  return (
                                    <Grid item xs={12} sm={6} md={4} key={config.id}>
                                      <Card sx={{ 
                                        borderRadius: 3, 
                                        border: `2px solid ${isSelected ? theme.palette.primary.main : alpha(theme.palette.grey[400], 0.8)}`,
                                        background: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'white',
                                        cursor: 'pointer',
                                        height: 280,
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                          transform: 'translateY(-2px)',
                                          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                                          border: `2px solid ${theme.palette.primary.main}`
                                        }
                                      }}
                                      onClick={() => {
                                        setSelectedUnitConfig(config);
                                        setNewRequest(prev => ({ ...prev, unit_configuration_id: config.id }));
                                        setUnitInputs({});
                                      }}
                                      >
                                        <CardContent sx={{ p: 3 }}>
                                          <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem', mb: 2 }}>
                                            {config.name}
                                          </Typography>
                                          
                                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.85rem' }}>
                                            {config.enable_cases && !config.enable_pallets && `${config.base_unit_name} → ${config.case_unit_name}`}
                                            {config.enable_cases && config.enable_pallets && `${config.base_unit_name} → ${config.case_unit_name} → ${config.pallet_unit_name}`}
                                            {!config.enable_cases && !config.enable_pallets && `${config.base_unit_name} only`}
                                          </Typography>

                                          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                            {config.enable_cases && (
                                              <Chip 
                                                label="Cases" 
                                                size="small" 
                                                sx={{
                                                  bgcolor: alpha(theme.palette.info.main, 0.1),
                                                  color: 'info.main',
                                                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                                  fontWeight: 500,
                                                  fontSize: '0.75rem',
                                                  height: 24
                                                }}
                                              />
                                            )}
                                            {config.enable_pallets && (
                                              <Chip 
                                                label="Pallets" 
                                                size="small" 
                                                sx={{
                                                  bgcolor: alpha(theme.palette.success.main, 0.1),
                                                  color: 'success.main',
                                                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                                  fontWeight: 500,
                                                  fontSize: '0.75rem',
                                                  height: 24
                                                }}
                                              />
                                            )}
                                          </Box>

                                          <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                                Units/Case
                                              </Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                                {config.enable_cases ? config.units_per_case : 'N/A'}
                                              </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                                Cases/Pallet
                                              </Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                                {config.enable_pallets ? config.cases_per_pallet : 'N/A'}
                                              </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                                Total/Pallet
                                              </Typography>
                                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '0.95rem' }}>
                                                {config.enable_pallets && config.enable_cases
                                                  ? (config.units_per_case * config.cases_per_pallet).toLocaleString()
                                                  : config.enable_cases
                                                  ? `${config.units_per_case}/case`
                                                  : '1'
                                                }
                                              </Typography>
                                            </Grid>
                                          </Grid>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* VARIANT DISTRIBUTION WITH UNIT INPUTS */}
                  {selectedUnitConfig && productVariants.length > 0 && (
                    <Grid item xs={12}>
                              <Card sx={{ 
                                p: 3, 
                        border: `1px solid ${theme.palette.grey[400]}`, 
                        borderRadius: 3, 
                        backgroundColor: 'white',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                        }
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary }}>
                          Variant Quantities
                        </Typography>
                        
                        <Grid container spacing={3}>
                          {productVariants.map((variant) => (
                            <Grid item xs={12} key={variant.id}>
                              <Card sx={{ 
                                p: 4, 
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                borderRadius: 3,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                backdropFilter: 'blur(20px)',
                                transition: 'all 0.3s ease',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                  borderColor: theme.palette.primary.main
                                }
                              }}>
                                <Typography variant="h5" sx={{ fontWeight: 500, color: '#000000', mb: 3 }}>
                                  {variant.volume_weight}{variant.volume_unit} Production
                                  {variant.sku && (
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 500, ml: 2 }}>
                                      (SKU: {variant.sku})
                                    </Typography>
                                  )}
                                </Typography>

                                <Grid container spacing={3}>
                                  {selectedUnitConfig.enable_pallets && (
                                    <Grid item xs={12} sm={4}>
                                  <TextField
                                    fullWidth
                                        label={`${(selectedUnitConfig.pallet_unit_name || 'Pallets').charAt(0).toUpperCase() + (selectedUnitConfig.pallet_unit_name || 'pallets').slice(1)} to Make`}
                                    type="number"
                                        value={unitInputs[variant.id]?.pallets || ''}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          // Only allow integers (no decimals)
                                          if (value === '' || /^\d+$/.test(value)) {
                                            const newPallets = value === '' ? 0 : parseInt(value, 10);
                                            const newInputs = { ...(unitInputs[variant.id] || {}), pallets: newPallets };
                                            setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                                            updateVariantFromUnitInputs(variant.id, newInputs);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          // Prevent decimal point and comma
                                          if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                            e.preventDefault();
                                          }
                                        }}
                                    inputProps={{ min: 0, step: 1 }}
                                    placeholder="0"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            backdropFilter: 'blur(10px)',
                                            '& fieldset': {
                                              borderColor: alpha(theme.palette.primary.main, 0.3),
                                              borderWidth: 1
                                            },
                                            '&:hover fieldset': {
                                              borderColor: theme.palette.primary.main
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: theme.palette.primary.main
                                            }
                                      }
                                    }}
                                  />
                                    </Grid>
                                  )}

                                  {selectedUnitConfig.enable_cases && (
                                    <Grid item xs={12} sm={4}>
                                      <TextField
                                        fullWidth
                                        label={`${(selectedUnitConfig.case_unit_name || 'Cases').charAt(0).toUpperCase() + (selectedUnitConfig.case_unit_name || 'cases').slice(1)} to Make`}
                                        type="number"
                                        value={unitInputs[variant.id]?.cases || ''}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          // Only allow integers (no decimals)
                                          if (value === '' || /^\d+$/.test(value)) {
                                            const newCases = value === '' ? 0 : parseInt(value, 10);
                                            const newInputs = { ...(unitInputs[variant.id] || {}), cases: newCases };
                                            setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                                            updateVariantFromUnitInputs(variant.id, newInputs);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          // Prevent decimal point and comma
                                          if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                            e.preventDefault();
                                          }
                                        }}
                                        inputProps={{ min: 0, step: 1 }}
                                        placeholder="0"
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            backdropFilter: 'blur(10px)',
                                            '& fieldset': {
                                              borderColor: alpha(theme.palette.primary.main, 0.3),
                                              borderWidth: 1
                                            },
                                            '&:hover fieldset': {
                                              borderColor: theme.palette.primary.main
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: theme.palette.primary.main
                                            }
                                          }
                                        }}
                                      />
                                    </Grid>
                                  )}

                                  <Grid item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      label={`${(selectedUnitConfig.base_unit_name || 'Units').charAt(0).toUpperCase() + (selectedUnitConfig.base_unit_name || 'units').slice(1)} to Make`}
                                      type="number"
                                      value={unitInputs[variant.id]?.units || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        // Only allow integers (no decimals)
                                        if (value === '' || /^\d+$/.test(value)) {
                                          const newUnits = value === '' ? 0 : parseInt(value, 10);
                                          const newInputs = { ...(unitInputs[variant.id] || {}), units: newUnits };
                                          setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                                          updateVariantFromUnitInputs(variant.id, newInputs);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        // Prevent decimal point and comma
                                        if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                          e.preventDefault();
                                        }
                                      }}
                                      inputProps={{ min: 0, step: 1 }}
                                      placeholder="0"
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                          backdropFilter: 'blur(10px)',
                                          '& fieldset': {
                                            borderColor: alpha(theme.palette.primary.main, 0.3),
                                            borderWidth: 1
                                          },
                                          '&:hover fieldset': {
                                            borderColor: theme.palette.primary.main
                                          },
                                          '&.Mui-focused fieldset': {
                                            borderColor: theme.palette.primary.main
                                          }
                                        }
                                      }}
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                <Box sx={{ 
                                      backgroundColor: '#f8fafc',
                                      borderRadius: 2,
                                      p: 3,
                                      border: '1px solid #e0e0e0',
                                  textAlign: 'center'
                                }}>
                                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 1 }}>
                                        Total: {calculateTotalUnitsFromInputs(variant.id)} {selectedUnitConfig.base_unit_name || 'units'}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                        = {(calculateTotalUnitsFromInputs(variant.id) * variant.volume_weight).toLocaleString()} {variant.volume_unit} production volume
                                  </Typography>
                                </Box>
                                  </Grid>
                                </Grid>
                              </Card>
                            </Grid>
                          ))}
                          
                          {calculateTotalProduction().totalVolume > 0 && (
                            <Grid item xs={12}>
                              <Card sx={{ 
                                p: 3, 
                                backgroundColor: alpha(theme.palette.success.main, 0.08),
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                              }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 1 }}>
                                  Total Request: {calculateTotalProduction().totalUnits} {selectedUnitConfig.base_unit_name || 'units'} = {calculateTotalProduction().totalVolume.toLocaleString()} {calculateTotalProduction().productionUnit}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  {newRequest.variant_distribution?.length || 0} variant size{newRequest.variant_distribution?.length === 1 ? '' : 's'} configured
                                </Typography>
                              </Card>
                            </Grid>
                          )}
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* Request Details */}
                  <Grid item xs={12}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[400]}`, 
                      borderRadius: 3, 
                      backgroundColor: 'white',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                      }
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Request Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Production Location *"
                            value={newRequest.production_location ? locations.find(l => l.id === newRequest.production_location)?.name || '' : ''}
                            placeholder="Click to select location..."
                            onClick={handleOpenLocationSelection}
                            InputProps={{
                              readOnly: true,
                              sx: {
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                }
                              }
                            }}
                            sx={{
                              cursor: 'pointer',
                              '& .MuiOutlinedInput-root': {
                                cursor: 'pointer'
                              },
                              '& .MuiInputBase-input': {
                                cursor: 'pointer'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Priority</InputLabel>
                            <Select
                              value={newRequest.priority}
                              label="Priority"
                              onChange={(e) => setNewRequest(prev => ({ ...prev, priority: e.target.value }))}
                            >
                              <MenuItem value="low">Low</MenuItem>
                              <MenuItem value="normal">Normal</MenuItem>
                              <MenuItem value="high">High</MenuItem>
                              <MenuItem value="urgent">Urgent</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DatePicker
                            label="Needed By Date"
                            value={newRequest.needed_by_date}
                            onChange={(date) => setNewRequest(prev => ({ ...prev, needed_by_date: date }))}
                            slotProps={{
                              textField: {
                                fullWidth: true
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={newRequest.status}
                              label="Status"
                              disabled={!canApproveManufacturingOrders}
                              onChange={(e) => setNewRequest(prev => ({ ...prev, status: e.target.value }))}
                            >
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="rejected">Rejected</MenuItem>
                              <MenuItem value="in_progress">In Progress</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Reason for Request *"
                            value={newRequest.reason}
                            onChange={(e) => setNewRequest(prev => ({ ...prev, reason: e.target.value }))}
                            multiline
                            rows={3}
                            placeholder="Explain why this manufacturing order is needed..."
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
          }}>
            <Button 
              onClick={() => {
                setCreateDialogOpen(false);
                setEditingRequest(null);
                setIsEditing(false);
                setErrorMessage('');
                setProductVariants([]);
              }}
              startIcon={<CloseIcon />}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                color: theme.palette.text.secondary,
                fontWeight: 500,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Cancel
            </Button>
            
            <Button 
              onClick={handleSaveRequest}
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={saving}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                color: 'white',
                fontWeight: 500,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                },
                '&:disabled': {
                  background: theme.palette.grey[400],
                  color: theme.palette.grey[100]
                }
              }}
            >
              {isEditing ? 'Update Request' : 'Create Request'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Dialog */}
        <Dialog 
          open={viewDialogOpen} 
          onClose={() => {
            setViewDialogOpen(false);
            setViewingRequest(null);
          }}
          maxWidth="md"
          fullWidth
          disableScrollLock={true}
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
              maxHeight: '90vh',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 500,
            fontSize: '1.2rem',
            color: 'primary.main',
            borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: alpha(theme.palette.primary.main, 0.05),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3
          }}>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Manufacturing Order Details
            </Typography>
            <IconButton 
              onClick={() => {
                setViewDialogOpen(false);
                setViewingRequest(null);
              }}
              size="small"
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  transform: 'scale(1.1)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ 
            backgroundColor: alpha(theme.palette.grey[50], 0.5), 
            p: 3,
            pt: 4,
            maxHeight: '70vh', 
            overflowY: 'auto',
            backdropFilter: 'blur(10px)',
            mt: 3
          }}>
            {viewingRequest && (
              <Grid container spacing={3} sx={{ mt: 0.5 }}>
                {/* Product Information */}
                    <Grid item xs={12}>
                      <Card sx={{ 
                        p: 3, 
                        border: `1px solid ${theme.palette.grey[400]}`, 
                        borderRadius: 3, 
                        backgroundColor: 'white',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                        }
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                          Product Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Product
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {(() => {
                                  const product = products.find(p => p.id === viewingRequest.product_id);
                                  if (!product) return 'Unknown Product';
                                  
                                  // Check if there are variants in the distribution
                                  if (viewingRequest.variant_distribution && Array.isArray(viewingRequest.variant_distribution) && viewingRequest.variant_distribution.length > 0) {
                                    const variantNames = viewingRequest.variant_distribution
                                      .map(v => {
                                        // Try to find matching variant to get volume_weight + volume_unit format
                                        const matchingVariant = productVariants.find(pv => pv.id === v.variant_id);
                                        if (matchingVariant) {
                                          return `${matchingVariant.volume_weight}${matchingVariant.volume_unit}`;
                                        }
                                        // Fallback to stored data
                                        return v.variant_name || v.name || v.size || `${v.volume_weight || ''}${v.volume_unit || ''}`;
                                      })
                                      .filter(name => name && name.trim())
                                      .join(', ');
                                    if (variantNames) {
                                      return `${product.name} (${variantNames})`;
                                    }
                                  }
                                  
                                  return product.name;
                                })()}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Formulation
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {(() => {
                                  const formulation = formulations.find(f => f.id === viewingRequest.formulation_id);
                                  return formulation ? `${formulation.name} v${formulation.version}` : 'N/A';
                                })()}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Requested Quantity
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formatQuantityWithBreakdown(viewingRequest)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Production Location
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {locations.find(l => l.id === viewingRequest.production_location)?.name || 'N/A'}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>

                    {/* Unit Configuration & Variant Breakdown */}
                    {viewingRequest.unit_configuration_id && (
                      <Grid item xs={12}>
                        <Card sx={{ 
                          p: 3, 
                          border: `1px solid ${theme.palette.grey[400]}`, 
                          borderRadius: 3, 
                          backgroundColor: 'white',
                          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                          }
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                            Production Configuration
                          </Typography>
                          <Grid container spacing={2}>
                            {(() => {
                              const config = unitConfigurations.find(c => c.id === viewingRequest.unit_configuration_id);
                              if (!config) return (
                                <Grid item xs={12}>
                                  <Typography variant="body2" color="text.secondary">
                                    Configuration not found
                                  </Typography>
                                </Grid>
                              );

                              return (
                                <>
                                  <Grid item xs={12} md={6}>
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Configuration Name
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {config.name}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Structure
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {config.enable_cases && !config.enable_pallets && `${config.base_unit_name} → ${config.case_unit_name}`}
                                        {config.enable_cases && config.enable_pallets && `${config.base_unit_name} → ${config.case_unit_name} → ${config.pallet_unit_name}`}
                                        {!config.enable_cases && !config.enable_pallets && `${config.base_unit_name} only`}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  {config.enable_cases && (
                                    <Grid item xs={12} md={4}>
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                          Units per {config.case_unit_name || 'Case'}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                          {config.units_per_case}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {config.enable_pallets && (
                                    <>
                                      <Grid item xs={12} md={4}>
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            {config.case_unit_name || 'Cases'} per {config.pallet_unit_name || 'Pallet'}
                                          </Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {config.cases_per_pallet}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Total per {config.pallet_unit_name || 'Pallet'}
                                          </Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                            {(config.units_per_case * config.cases_per_pallet).toLocaleString()} {config.base_unit_name}
                                          </Typography>
                                        </Box>
                    </Grid>
                                    </>
                                  )}
                                </>
                              );
                            })()}
                          </Grid>
                        </Card>
                      </Grid>
                    )}

                    {/* Variant Breakdown */}
                    {viewingRequest.variant_distribution && viewingRequest.variant_distribution.length > 0 && viewingRequest.unit_configuration_id && (
                      <Grid item xs={12}>
                        <Card sx={{ 
                          p: 3, 
                          border: `1px solid ${theme.palette.grey[400]}`, 
                          borderRadius: 3, 
                          backgroundColor: 'white',
                          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                          }
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary }}>
                            Variant Breakdown
                          </Typography>
                          <Grid container spacing={2}>
                            {viewingRequest.variant_distribution.map((variantDist) => {
                              const variant = productVariants.find(v => v.id === variantDist.variant_id);
                              const config = unitConfigurations.find(c => c.id === viewingRequest.unit_configuration_id);
                              
                              if (!variant || !config) return null;

                              const totalUnits = variantDist.quantity || 0;
                              
                              // Calculate breakdown
                              let remaining = totalUnits;
                              let pallets = 0;
                              let cases = 0;
                              let units = 0;

                              if (config.enable_pallets && config.enable_cases) {
                                const unitsPerPallet = config.units_per_case * config.cases_per_pallet;
                                pallets = Math.floor(remaining / unitsPerPallet);
                                remaining = remaining % unitsPerPallet;
                              }

                              if (config.enable_cases) {
                                cases = Math.floor(remaining / config.units_per_case);
                                remaining = remaining % config.units_per_case;
                              }

                              units = remaining;

                              return (
                                <Grid item xs={12} key={variant.id}>
                                  <Box sx={{ 
                                    p: 3, 
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    borderRadius: 2,
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`
                                  }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                                      {variant.volume_weight}{variant.volume_unit}
                                      {variant.sku && (
                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 500, ml: 2 }}>
                                          (SKU: {variant.sku})
                                        </Typography>
                                      )}
                                    </Typography>

                                    <Grid container spacing={2}>
                                      {config.enable_pallets && pallets > 0 && (
                                        <Grid item xs={12} sm={4}>
                                          <Box sx={{ 
                                            p: 2, 
                                            borderRadius: 2, 
                                            bgcolor: alpha(theme.palette.success.main, 0.1),
                                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                                          }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                              {config.pallet_unit_name || 'Pallets'}
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 500, color: 'success.main' }}>
                                              {pallets}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}

                                      {config.enable_cases && cases > 0 && (
                                        <Grid item xs={12} sm={4}>
                                          <Box sx={{ 
                                            p: 2, 
                                            borderRadius: 2, 
                                            bgcolor: alpha(theme.palette.info.main, 0.1),
                                            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                                          }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                              {config.case_unit_name || 'Cases'}
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 500, color: 'info.main' }}>
                                              {cases}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}

                                      {units > 0 && (
                                        <Grid item xs={12} sm={4}>
                                          <Box sx={{ 
                                            p: 2, 
                                            borderRadius: 2, 
                                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                                            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                                          }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                              {config.base_unit_name || 'Units'}
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 500, color: 'warning.main' }}>
                                              {units}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}

                                      <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Total: {totalUnits.toLocaleString()} {config.base_unit_name || 'units'} = {(totalUnits * variant.volume_weight).toLocaleString()} {variant.volume_unit}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Card>
                      </Grid>
                    )}

                {/* Request Details */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    p: 3, 
                    border: `1px solid ${theme.palette.grey[400]}`, 
                    borderRadius: 3, 
                    backgroundColor: 'white',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    }
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                      Request Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Status
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip 
                              label={viewingRequest.status?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
                              size="small"
                              sx={{ 
                                fontWeight: 500,
                                backgroundColor: getStatusColor(viewingRequest.status),
                                color: 'white'
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Priority
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip 
                              label={viewingRequest.priority?.toUpperCase() || 'NORMAL'}
                              size="small"
                              sx={{ 
                                fontWeight: 500,
                                backgroundColor: getPriorityColor(viewingRequest.priority),
                                color: 'white'
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>
                      {viewingRequest.needed_by_date && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Needed By
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {dayjs(viewingRequest.needed_by_date).format('MMM D, YYYY')}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Reason
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {viewingRequest.reason || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                {/* Approval Information */}
                {viewingRequest.status === 'approved' && viewingRequest.approved_by_signature && (
                  <Grid item xs={12}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.success.main}`, 
                      borderRadius: 3, 
                      backgroundColor: alpha(theme.palette.success.main, 0.05),
                      boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.15)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.25)}`,
                        border: `1px solid ${theme.palette.success.main}`
                      }
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'success.main' }}>
                        Approval Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Approved By
                            </Typography>
                            <Box sx={{ 
                              border: `1px solid ${theme.palette.success.main}`, 
                              p: 2, 
                              borderRadius: 2, 
                              bgcolor: 'white',
                              mt: 1
                            }}>
                              <Box sx={{ 
                                fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                fontSize: '20px', 
                                color: theme.palette.text.primary,
                                textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                mb: 1
                              }}>
                                {viewingRequest.approved_by_signature}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Electronic Signature
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Approved At
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {viewingRequest.approved_at ? dayjs(viewingRequest.approved_at).format('MMM D, YYYY HH:mm') : 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>
                        {viewingRequest.approval_notes && (
                          <Grid item xs={12}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Approval Notes
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {viewingRequest.approval_notes}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
          }}>
            <Button 
              onClick={() => {
                setViewDialogOpen(false);
                setViewingRequest(null);
              }}
              startIcon={<CloseIcon />}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                color: theme.palette.text.secondary,
                fontWeight: 500,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Close
            </Button>
            
             {canEditManufacturingOrders && (
               <Button 
                 onClick={() => handleEditRequest(viewingRequest)}
                 variant="contained"
                 startIcon={<EditIcon />}
                 sx={{
                   borderRadius: 2,
                   px: 4,
                   py: 1.5,
                   background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                   color: 'white',
                   fontWeight: 500,
                   boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                   '&:hover': {
                     background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                     boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                   }
                 }}
               >
                 Edit Request
               </Button>
             )}
          </DialogActions>
        </Dialog>

        {/* Product Selection Dialog */}
        <Dialog
          open={productSelectionDialogOpen}
          onClose={() => setProductSelectionDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          disableScrollLock={true}
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
              height: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 500,
            fontSize: '1.2rem',
            color: 'primary.main',
            borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: alpha(theme.palette.primary.main, 0.05),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            Select Product
            <IconButton 
              onClick={() => setProductSelectionDialogOpen(false)} 
              size="small"
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  transform: 'scale(1.1)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search products by name, description, or SKU..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                InputProps={{
                  sx: {
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    fontSize: '0.9rem',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
              />
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      background: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}>
                      Product
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      background: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      width: 100
                    }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredProducts().length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          {productSearchTerm ? 'No products found matching your search.' : 'No products available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    getFilteredProducts().map((product) => (
                      <TableRow
                        key={product.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                        onClick={() => handleSelectProduct(product)}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                              {product.name}
                            </Typography>
                            {product.sku && (
                              <Typography variant="caption" color="text.secondary">
                                SKU: {product.sku}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectProduct(product);
                            }}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              px: 2,
                              py: 0.5,
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                              }
                            }}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <Button 
              onClick={() => setProductSelectionDialogOpen(false)}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                px: 3,
                py: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => window.open(`/company/${companyId}/products`, '_blank')}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                px: 3,
                py: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }}
            >
              Add Product
            </Button>
          </DialogActions>
        </Dialog>

        {/* Formulation Selection Dialog */}
        <Dialog
          open={formulationSelectionDialogOpen}
          onClose={() => setFormulationSelectionDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          disableScrollLock={true}
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
              height: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 500,
            fontSize: '1.2rem',
            color: 'primary.main',
            borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: alpha(theme.palette.primary.main, 0.05),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            Select Formulation
            <IconButton 
              onClick={() => setFormulationSelectionDialogOpen(false)} 
              size="small"
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  transform: 'scale(1.1)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search formulations by name, version, or description..."
                value={formulationSearchTerm}
                onChange={(e) => setFormulationSearchTerm(e.target.value)}
                InputProps={{
                  sx: {
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    fontSize: '0.9rem',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
              />
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      background: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}>
                      Formulation
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      background: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}>
                      Base Size
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      background: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      width: 100
                    }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredFormulations().length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          {formulationSearchTerm ? 'No formulations found matching your search.' : 'No formulations available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    getFilteredFormulations().map((formulation) => (
                      <TableRow
                        key={formulation.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                        onClick={() => handleSelectFormulation(formulation)}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                              {formulation.name} v{formulation.version}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Version: {formulation.version}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {formulation.target_batch_size ? `${formulation.target_batch_size} ${formulation.unit_of_measure || 'g'}` : 'No base size'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectFormulation(formulation);
                            }}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              px: 2,
                              py: 0.5,
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                              }
                            }}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <Button 
              onClick={() => setFormulationSelectionDialogOpen(false)}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                px: 3,
                py: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => window.open(`/company/${companyId}/formulations`, '_blank')}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                px: 3,
                py: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }}
            >
              Add Formulation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Location Selection Dialog */}
        <Dialog
          open={locationSelectionDialogOpen}
          onClose={() => setLocationSelectionDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          disableScrollLock={true}
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
              height: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 500,
            fontSize: '1.2rem',
            color: 'primary.main',
            borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: alpha(theme.palette.primary.main, 0.05),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            Select Production Location
            <IconButton 
              onClick={() => setLocationSelectionDialogOpen(false)} 
              size="small"
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  transform: 'scale(1.1)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search locations by name, type, city, or state..."
                value={locationSearchTerm}
                onChange={(e) => setLocationSearchTerm(e.target.value)}
                InputProps={{
                  sx: {
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    fontSize: '0.9rem',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
              />
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      background: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}>
                      Location
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      background: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}>
                      Type & Address
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      background: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      width: 100
                    }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredLocations().length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          {locationSearchTerm ? 'No locations found matching your search.' : 'No locations available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    getFilteredLocations().map((location) => (
                      <TableRow
                        key={location.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                        onClick={() => handleSelectLocation(location)}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                              {location.name}
                            </Typography>
                            {location.facility_type && (
                              <Typography variant="caption" color="text.secondary">
                                Type: {location.facility_type}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {[location.city, location.state].filter(Boolean).join(', ') || 'No address'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectLocation(location);
                            }}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              px: 2,
                              py: 0.5,
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                              }
                            }}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <Button 
              onClick={() => setLocationSelectionDialogOpen(false)}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                px: 3,
                py: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => window.open(`/company/${companyId}/locations`, '_blank')}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                px: 3,
                py: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }}
            >
              Add Location
            </Button>
          </DialogActions>
        </Dialog>

        {/* Approval Dialog */}
        <Dialog 
          open={approvalDialogOpen} 
          onClose={() => setApprovalDialogOpen(false)}
          maxWidth="md"
          fullWidth
          disableScrollLock={true}
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 500,
            fontSize: '1.2rem',
            color: 'primary.main',
            borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: alpha(theme.palette.primary.main, 0.05),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircleIcon sx={{ color: 'success.main' }} />
              <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                Approve Manufacturing Order
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setApprovalDialogOpen(false)} 
              size="small"
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  transform: 'scale(1.1)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ 
            backgroundColor: alpha(theme.palette.grey[50], 0.5), 
            p: 3,
            maxHeight: '70vh', 
            overflowY: 'auto',
            mt: 4
          }}>
            {approvalRequest && (
              <Grid container spacing={3}>
                {/* Request Summary */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    p: 3, 
                    border: `1px solid ${theme.palette.grey[400]}`, 
                    borderRadius: 3, 
                    backgroundColor: 'white',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                      Request Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Product
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {products.find(p => p.id === approvalRequest.product_id)?.name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Quantity
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formatQuantityWithBreakdown(approvalRequest)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Needed By
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {approvalRequest.needed_by_date ? dayjs(approvalRequest.needed_by_date).format('MMM D, YYYY') : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Priority
                        </Typography>
                        <Chip 
                          label={approvalRequest.priority?.toUpperCase() || 'NORMAL'}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Reason
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {approvalRequest.reason || 'No reason provided'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                {/* Signature Section */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    p: 3, 
                    border: `1px solid ${theme.palette.grey[400]}`, 
                    borderRadius: 3, 
                    backgroundColor: 'white',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                      Approval Signature
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Your Name (Electronic Signature) *"
                          value={signatureName}
                          onChange={(e) => setSignatureName(e.target.value)}
                          required
                          placeholder="Enter your full name to approve this request"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2
                            }
                          }}
                        />
                      </Grid>
                      
                      {signatureName && (
                        <Grid item xs={12}>
                          <Box sx={{ 
                            border: `1px solid ${theme.palette.primary.main}`, 
                            p: 3, 
                            borderRadius: 2, 
                            bgcolor: alpha(theme.palette.primary.main, 0.05)
                          }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                              ELECTRONIC SIGNATURE PREVIEW:
                            </Typography>
                            <Box sx={{ 
                              fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                              fontSize: '24px', 
                              color: theme.palette.text.primary,
                              textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                              mb: 1
                            }}>
                              {signatureName}
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Signed: {dayjs().format('MMM DD, YYYY HH:mm')}
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Approval Notes (Optional)"
                          value={approvalNotes}
                          onChange={(e) => setApprovalNotes(e.target.value)}
                          placeholder="Add any notes or comments about this approval..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ borderRadius: 2, fontWeight: 500 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <strong>Note:</strong> Approving this manufacturing order will automatically create a batch entry in the production calendar for {approvalRequest.needed_by_date ? dayjs(approvalRequest.needed_by_date).format('MMM D, YYYY') : 'the requested date'}.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
          }}>
            <Button 
              onClick={() => setApprovalDialogOpen(false)}
              startIcon={<CloseIcon />}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                color: theme.palette.text.secondary,
                fontWeight: 500,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Cancel
            </Button>
            
            <Button 
              onClick={handleApprovalSubmit}
              variant="contained"
              startIcon={<CheckCircleIcon />}
              disabled={!signatureName.trim()}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                color: 'white',
                fontWeight: 500,
                boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`
                },
                '&:disabled': {
                  background: theme.palette.grey[400],
                  color: theme.palette.grey[100]
                }
              }}
            >
              Approve Request
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          disableScrollLock={true}
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 500,
            fontSize: '1.2rem',
            color: 'primary.main',
            borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: alpha(theme.palette.primary.main, 0.05),
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <DeleteIcon />
            Confirm Delete Request
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500, mt: 2 }}>
              Are you sure you want to delete this manufacturing order?
            </Typography>
            
            {deleteRequest && (
              <Card sx={{ 
                bgcolor: alpha(theme.palette.grey[50], 0.5),
                border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                mb: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 500, mb: 1 }}>
                    Manufacturing Order Details:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Product:</strong> {(() => {
                      const product = products.find(p => p.id === deleteRequest.product_id);
                      if (!product) return 'Unknown Product';
                      
                      // Check if there are variants in the distribution
                      if (deleteRequest.variant_distribution && Array.isArray(deleteRequest.variant_distribution) && deleteRequest.variant_distribution.length > 0) {
                        const variantNames = deleteRequest.variant_distribution
                          .map(v => {
                            // Try to find matching variant to get volume_weight + volume_unit format
                            const matchingVariant = productVariants.find(pv => pv.id === v.variant_id);
                            if (matchingVariant) {
                              return `${matchingVariant.volume_weight}${matchingVariant.volume_unit}`;
                            }
                            // Fallback to stored data
                            return v.variant_name || v.name || v.size || `${v.volume_weight || ''}${v.volume_unit || ''}`;
                          })
                          .filter(name => name && name.trim())
                          .join(', ');
                        if (variantNames) {
                          return `${product.name} (${variantNames})`;
                        }
                      }
                      
                      return product.name;
                    })()}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Quantity:</strong> {formatQuantityWithBreakdown(deleteRequest)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Needed By:</strong> {deleteRequest.needed_by_date || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Reason:</strong> {deleteRequest.reason || 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            )}
            
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone. The request will be permanently removed from the system.
            </Typography>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <Button 
              onClick={() => setDeleteDialogOpen(false)}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                px: 3,
                py: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteRequest}
              variant="contained"
              color="error"
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                px: 3,
                py: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.4)}`
                }
              }}
            >
              Delete Request
            </Button>
          </DialogActions>
        </Dialog>
          </Container>
        </LocalizationProvider>
      </Box>
    </Fade>
  );
};

export default BatchingRequests;